import styled from 'styled-components/macro';
import { func, number, bool } from 'prop-types';
import last from 'lodash.last';
import { utcFriday } from 'd3-time';
import isThisWeek from 'date-fns/isThisWeek';

import { Label } from './shared.style';

const ThisWeekBackground = styled('rect')`
  fill: ${({ theme }) => theme.Grey};
  fill-opacity: 0.15;
`;

/**
 * ThisWeek shows two labels designating which weeks are This Week and This Week To Date. Both
 * are domain-specific terms used by Warner to describe recent chart weeks. It also shows a highlighted
 * background for This Week To Date, to give additional emphasis.
 *
 * @export
 * @param {any} { x, yPos, height, isSmallScreen }
 * @returns {React.Component}
 */
/** @component */
export default function ThisWeek({
  height,
  isComplementary,
  isSmallScreen,
  topMargin,
  x,
  yPos,
}) {
  const xMinusOneWeek = x(utcFriday.offset(last(x.domain()), -1));
  const labelOffset = -6;
  const weekIsThisWeek = isThisWeek(last(x.domain()), { weekStartsOn: 6 });
  const xMinusTwoWeeks = x(utcFriday.offset(last(x.domain()), -2));
  const xDifference = Math.abs(xMinusOneWeek - xMinusTwoWeeks);
  const isScaleSmall = xDifference < 100;

  return weekIsThisWeek ? (
    <>
      {!isComplementary && (
        <>
          <Label x={xMinusOneWeek} y={yPos + topMargin} dy={labelOffset}>
            {isSmallScreen || isScaleSmall ? 'TWTD' : 'This Week To Date'}
          </Label>
          <Label x={xMinusTwoWeeks} y={yPos + topMargin} dy={labelOffset}>
            {isSmallScreen || isScaleSmall ? 'LW' : 'Last Full Week'}
          </Label>
        </>
      )}
      <ThisWeekBackground
        x={xMinusOneWeek}
        y={yPos + topMargin}
        width={x(last(x.domain())) - xMinusOneWeek}
        height={height - yPos}
      />
    </>
  ) : null;
}

ThisWeek.defaultProps = {
  isSmallScreen: true,
};

ThisWeek.propTypes = {
  /** A configured d3-scale function */
  x: func.isRequired,
  /** The y position for the labels */
  yPos: number.isRequired,
  /** The height of the This Week To Date background */
  height: number.isRequired,
  /** Are we on mobile? */
  isSmallScreen: bool,
  /** Is this chart a complementary chart to another performance chart? */
  isComplementary: bool,
  topMargin: number.isRequired,
};
